import { Component, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { TvDetailsService } from '../../../../core/services/tv/tv.service';
import { LanguageService } from '../../../../core/services/language/language.service';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { YoutubePlayerService } from '../../../../core/services/youtube.service';
import { TeaserTrailerService } from '../../../../core/services/trailers.service';
import ColorThief from 'colorthief'
import { Input } from '@angular/core';
import { Utils } from '../../../../shared/utils';
import { DataService } from '../../../../shared/data.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SeoService } from '../../../../core/services/seo/seo.service';

@Component({
  selector: 'app-tv-details',
  imports: [CommonModule],
  templateUrl: './tv-details.html',
  styleUrl: './tv-details.scss'
})
export class TvDetails implements OnInit, OnDestroy {
  private seoService = inject(SeoService);
  private destroy$ = new Subject<void>();
  @Output() tvDetailsEvent = new EventEmitter<string>();
  @Input() trailerDiv!: ElementRef<HTMLDivElement>;
  @Input() showPlayer: boolean = false;
  @Output() showPlayerChange = new EventEmitter<boolean>();

  tvId: string | null = null;
  tvName: string | null = null;
  tvData: any = null;
  isLoading: boolean = true;
  posterLoaded: boolean = false;
  frontendServer: any = environment.frontendUrl;
  backendServer: string = environment.backendUrl;
  trailerId: string = "";
  dominantColor: string = ''
  palette: any[] = []
  middleColor: string = '';
  encodeURIComponent = encodeURIComponent;


  @ViewChild('tvDetailContainer') tvDetailContainer!: ElementRef<HTMLImageElement>;
  @ViewChild('tvBackdropImage') tvBackdropImage!: ElementRef<HTMLDivElement>;
  @ViewChild('trailerPlayer') trailerPlayer!: ElementRef<HTMLDivElement>;

  constructor(
    private tvDetailsService: TvDetailsService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute,
    private youtubeService: YoutubePlayerService,
    private trailerService: TeaserTrailerService,
    private utils: Utils,
    private dataService: DataService
  ) { }

  public readonly languageService = inject(LanguageService);

  ngOnInit() {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.fetchTvDetails(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.tvId) {
        this.fetchTvDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.youtubeService.destroyPlayer();
  }

  onImageError(event: Event) {
    this.posterLoaded = true;
    return this.utils.onImageVerticalError(event);
  }

  onImageLoad(img: HTMLImageElement) {

    const colorThief = new ColorThief();
    const color = colorThief.getColor(img);
    this.dominantColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

    const palette = colorThief.getPalette(img, 5);
    this.palette = palette.map((c: any) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`);

    const middleIndex = Math.floor(this.palette.length / 2)
    this.middleColor = this.palette[middleIndex]
  }


  fetchTvDetails(slugParam?: string | null) {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');

    if (slug) {
      const [id, name] = slug.split('-');
      if (this.tvId !== id) {
        this.isLoading = true;
        this.posterLoaded = false;
        this.tvData = null;
      }
      this.tvId = id;
      this.tvName = name;
    }

    if (this.tvId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.tvDetailsService.getTvDetails(parseInt(this.tvId), lang).subscribe({
        next: (data) => {
          this.tvData = data;
          this.isLoading = false;
          this.dataService.sendMessage(this.tvData);
          const currentSlug = slug || this.tvId || '';
          this.seoService.setTvSeo(data, currentSlug);

          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        },
        error: (err) => {
          console.error('Failed to load TV details:', err);
          this.isLoading = false;
        }
      });
    }
  }

  async playTrailer(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    // Emit to parent to show modal
    this.showPlayer = true;
    this.showPlayerChange.emit(true);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    this.trailerService
      .getTeaserOrTrailers(parseInt(this.tvId ?? ''), 'tv')
      .subscribe(trailers => {
        const trailer = trailers.results.find((t: any) =>
          t.type === 'Trailer' &&
          (t.official === true || t.official === false)
        );

        if (trailer) {
          this.trailerId = trailer.key;

          setTimeout(() => {
            this.youtubeService.playTrailer(this.trailerId, 'trailer-player');
          }, 300);

          if (this.trailerDiv?.nativeElement) {
            this.trailerDiv.nativeElement.style.display = 'block';
          }
        } else {
          // If no trailer found, close the modal
          this.showPlayer = false;
          this.showPlayerChange.emit(false);
          document.body.style.overflow = '';
        }
      });
  }

  filterRating(rating: number) {
    return this.utils.filerRatings(rating ?? 0);
  }

  triptContent(content: string) {
    return this.utils.tripContent(content);
  }
}
